// Plain history routing: a path pattern, a handler, and one place that catches what a page throws.
// Parents arrive on these URLs straight from a WhatsApp message, so they must work cold, on a phone,
// with no app and no login.

import { failed, mount } from "./dom.ts";
import { leavePage } from "./nav.ts";

export interface Route {
  pattern: RegExp;
  load: (params: Record<string, string>, query: URLSearchParams) => Promise<void> | void;
}

const routes: Route[] = [];

export function route(path: string, load: Route["load"]): void {
  const names: string[] = [];
  const pattern = new RegExp(
    "^" + path.replace(/:[a-zA-Z_]+/g, (m) => {
      names.push(m.slice(1));
      return "([^/]+)";
    }) + "/?$",
  );
  routes.push({
    pattern,
    load: (_p, q) => {
      const m = location.pathname.match(pattern)!;
      const params = Object.fromEntries(names.map((n, i) => [n, decodeURIComponent(m[i + 1] ?? "")]));
      return load(params, q);
    },
  });
}

export async function go(to: string, replace = false): Promise<void> {
  if (replace) history.replaceState({}, "", to);
  else history.pushState({}, "", to);
  await render();
}

/** Intercept in-site links so a tap doesn't reload the whole page. */
export function linkHandler(e: MouseEvent): void {
  const a = (e.target as Element).closest?.("a[href]") as HTMLAnchorElement | null;
  if (!a || a.target === "_blank" || a.hasAttribute("download") || e.metaKey || e.ctrlKey) return;
  const url = new URL(a.href, location.origin);
  if (url.origin !== location.origin) return;
  e.preventDefault();
  void go(url.pathname + url.search);
}

export async function render(): Promise<void> {
  // Anything the last page left running stops here. A shortlist still being polled would otherwise
  // paint itself over whichever page the person moved on to.
  leavePage();

  const q = new URLSearchParams(location.search);
  const match = routes.find((r) => r.pattern.test(location.pathname));
  try {
    if (!match) {
      const { notFound } = await import("./pages/not-found.ts");
      return notFound();
    }
    await match.load({}, q);
  } catch (err) {
    console.error(err);
    mount(failed(err instanceof Error ? err.message : "Something went wrong at our end."));
  }
}

export function startRouter(): void {
  document.addEventListener("click", linkHandler);
  addEventListener("popstate", () => void render());
  void render();
}
